/**
 * Compliance Agent — operational view of Sentinel's daily findings.
 *
 * This is the surface the sidebar "Compliance" entry now links to.
 * It's an HR-admin command center: every flagged employee, every
 * expiring certification, every COBRA deadline, every overdue
 * document, in one filterable list. Clicking a finding routes to
 * the right detail page (employee profile, COBRA dashboard, etc.).
 *
 * Distinct from /settings/compliance, which is the place to CONFIGURE
 * compliance requirements (what's required, renewal cadence, etc.).
 * The split mirrors how the Settings/Module dichotomy works
 * elsewhere in Lenavio — Settings is config, the module page is
 * day-to-day work.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  getComplianceFindings,
  type ComplianceFinding,
  type ComplianceReport,
} from "@/api/complianceAgent";


/**
 * Friendly labels for each Sentinel category — keys must match the
 * category strings the agent emits in its `findings` dict.
 */
const CATEGORY_LABELS: Record<string, { label: string; tone: "danger" | "warning" | "info" | "default" }> = {
  expired_documents: { label: "Expired documents", tone: "danger" },
  expiring_documents: { label: "Expiring documents", tone: "warning" },
  missing_signatures: { label: "Missing signatures", tone: "warning" },
  overdue_assignments: { label: "Overdue documents", tone: "warning" },
  compliance_expired: { label: "Expired certifications", tone: "danger" },
  compliance_expiring: { label: "Expiring certifications", tone: "warning" },
  missing_emergency_contacts: { label: "Missing emergency contacts", tone: "warning" },
  overtime_exposure: { label: "Overtime exposure", tone: "warning" },
  negative_leave_balance: { label: "Negative leave balances", tone: "warning" },
  stale_approvals: { label: "Stale approvals", tone: "warning" },
  incomplete_onboarding: { label: "Incomplete onboarding", tone: "warning" },
  no_recent_review: { label: "No recent performance review", tone: "info" },
  unreviewed_form_submissions: { label: "Unreviewed form submissions", tone: "warning" },
  stale_form_revisions: { label: "Stale form revisions", tone: "warning" },
  cobra_election_due: { label: "COBRA election due", tone: "warning" },
  cobra_election_overdue: { label: "COBRA election overdue", tone: "danger" },
  cobra_initial_payment_due: { label: "COBRA initial payment due", tone: "warning" },
  cobra_coverage_ending_soon: { label: "COBRA coverage ending soon", tone: "info" },
  carrier_invoice_discrepancies: { label: "Carrier invoice discrepancies", tone: "danger" },
};


function _label(category: string): { label: string; tone: "danger" | "warning" | "info" | "default" } {
  return (
    CATEGORY_LABELS[category] || {
      label: category.replace(/_/g, " "),
      tone: "default" as const,
    }
  );
}


/**
 * Build a click-through URL for a finding. Most findings carry an
 * employee_id; some COBRA / invoice findings carry their own ids.
 */
function _routeFor(f: ComplianceFinding): string | null {
  if (f.category?.startsWith("cobra")) return "/cobra";
  if (f.category === "carrier_invoice_discrepancies") {
    const id = (f as Record<string, unknown>).id;
    if (typeof id === "string") {
      return `/benefits/invoices?invoice=${id}`;
    }
    return "/benefits/invoices";
  }
  if (f.category === "unreviewed_form_submissions" && f.employee_id) {
    const aid = (f as Record<string, unknown>).assignment_id;
    if (typeof aid === "string") {
      return `/employees/${f.employee_id}/forms/${aid}`;
    }
  }
  if (f.employee_id) return `/employees/${f.employee_id}`;
  return null;
}


function _describe(f: ComplianceFinding): string {
  // Prefer the agent-provided message when present.
  if (typeof f.message === "string" && f.message.trim()) return f.message;
  const parts: string[] = [];
  if (f.employee_name) parts.push(String(f.employee_name));
  if (f.document_name) parts.push(String(f.document_name));
  if (f.days_until !== undefined && f.days_until !== null) {
    parts.push(`${f.days_until}d`);
  } else if (f.due_date) {
    parts.push(String(f.due_date));
  }
  return parts.join(" · ") || "Compliance finding";
}


export function CompliancePage() {
  const { addToast } = useToast();
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  const reload = async () => {
    setLoading(true);
    try {
      setReport(await getComplianceFindings());
    } catch (err) {
      console.warn("CompliancePage: failed to load findings", err);
      addToast(
        "warning",
        "Compliance Agent is initializing — try again in a minute.",
      );
      setReport({
        generated_at: new Date().toISOString(),
        summary: { total: 0, critical: 0, warning: 0, info: 0 },
        category_counts: {},
        findings: [],
        raw: {},
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!report) return [];
    if (filter === "all") return report.findings;
    return report.findings.filter((f) => f.category === filter);
  }, [report, filter]);

  const categories = useMemo(() => {
    if (!report) return [];
    return Object.entries(report.category_counts)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ key: k, count: v, ..._label(k) }));
  }, [report]);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Compliance Agent"
        subtitle={
          report?.generated_at
            ? `Last scan: ${new Date(report.generated_at).toLocaleString()}`
            : "Sentinel's daily compliance scan — surfaces overdue docs, expiring certifications, missing emergency contacts, COBRA deadlines, and carrier billing discrepancies."
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryTile
          icon={<AlertTriangle className="h-4 w-4 text-danger" />}
          label="Critical"
          value={report?.summary.critical ?? "—"}
          tone={report && report.summary.critical > 0 ? "danger" : undefined}
        />
        <SummaryTile
          icon={<Clock className="h-4 w-4 text-warning" />}
          label="Warning"
          value={report?.summary.warning ?? "—"}
          tone={report && report.summary.warning > 0 ? "warning" : undefined}
        />
        <SummaryTile
          icon={<FileText className="h-4 w-4 text-accent" />}
          label="Informational"
          value={report?.summary.info ?? "—"}
        />
        <SummaryTile
          icon={<ShieldCheck className="h-4 w-4 text-success" />}
          label="Total"
          value={report?.summary.total ?? "—"}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent" />
              Findings
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={reload}
              disabled={loading}
              title="Re-run scan"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              />
              Re-scan
            </Button>
          </div>
        </CardHeader>

        {/* Category chips — one per category that has findings. */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            <Chip
              active={filter === "all"}
              onClick={() => setFilter("all")}
              label={`All (${report?.summary.total ?? 0})`}
            />
            {categories.map((c) => (
              <Chip
                key={c.key}
                active={filter === c.key}
                onClick={() => setFilter(c.key)}
                label={`${c.label} (${c.count})`}
                tone={c.tone}
              />
            ))}
          </div>
        )}

        {loading && !report ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success" />
            <p className="text-text-primary font-medium">
              All clear.
            </p>
            <p className="text-text-muted mt-1">
              Sentinel didn&rsquo;t find anything to flag right now.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {filtered.map((f, idx) => {
              const meta = _label(f.category);
              const route = _routeFor(f);
              const desc = _describe(f);
              const Row = (
                <div className="flex items-center justify-between gap-3 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={meta.tone}>{meta.label}</Badge>
                      {f.severity === "critical" && (
                        <Badge variant="danger">Critical</Badge>
                      )}
                      <span className="text-sm text-text-primary truncate">
                        {desc}
                      </span>
                    </div>
                  </div>
                  {route && (
                    <ChevronRight className="h-4 w-4 text-text-muted shrink-0" />
                  )}
                </div>
              );
              return (
                <li key={`${f.category}-${idx}`}>
                  {route ? (
                    <Link
                      to={route}
                      className="block hover:bg-surface-hover/40 -mx-2 px-2 rounded-md"
                    >
                      {Row}
                    </Link>
                  ) : (
                    <div className="px-2">{Row}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Footer link to the configuration surface so admins can
          adjust which requirements get tracked. Different concept
          from this page (operational findings) — kept clearly
          separate. */}
      <div className="mt-4 text-sm text-text-muted">
        Configure tracked requirements at{" "}
        <Link
          to="/settings/compliance"
          className="text-accent hover:underline"
        >
          Settings → Compliance
        </Link>
        .
      </div>
    </motion.div>
  );
}

interface SummaryTileProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone?: "danger" | "warning";
}

function SummaryTile({ icon, label, value, tone }: SummaryTileProps) {
  const toneClass =
    tone === "danger"
      ? "border-danger/30 bg-danger/5"
      : tone === "warning"
        ? "border-warning/30 bg-warning/5"
        : "border-border/40 bg-surface/60";
  return (
    <div className={`rounded-md border ${toneClass} p-3`}>
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold text-text-primary">
        {value}
      </div>
    </div>
  );
}

interface ChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: "danger" | "warning" | "info" | "default";
}

function Chip({ label, active, onClick, tone }: ChipProps) {
  const dotColor =
    tone === "danger"
      ? "bg-danger"
      : tone === "warning"
        ? "bg-warning"
        : tone === "info"
          ? "bg-accent"
          : "bg-text-muted/60";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border transition-colors ${
        active
          ? "bg-accent text-white border-accent"
          : "border-border text-text-muted hover:bg-surface-hover"
      }`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
      {label}
    </button>
  );
}

export default CompliancePage;
